import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import timeSyncService from '../../features/sync/services/timeSyncService';

/**
 * TimeContext value
 */
export interface TimeContextValue {
  currentTimeMs: number;
  currentTimeSeconds: number;
  timeOffset: number;
  updateTimeOffset: (newOffset: number) => void;
  getTimeWithOffset: (offset: number) => number;
}

/**
 * TimeContext - Centralized time management for the application
 *
 * Provides a single timer that updates every second, eliminating the need
 * for multiple independent setInterval calls across components.
 *
 * The context provides:
 * - currentTimeSeconds: Server-synced time in seconds (with offset applied)
 * - currentTimeMs: Raw timestamp in milliseconds
 * - getTimeWithOffset: Function to get time with a custom offset
 */
const TimeContext = createContext<TimeContextValue | null>(null);

interface TimeProviderProps {
  children: ReactNode;
  updateInterval?: number;
}

/**
 * TimeProvider - Wraps the app and provides centralized time updates
 */
export function TimeProvider({ children, updateInterval = 1000 }: TimeProviderProps): React.ReactElement {
  const [currentTimeMs, setCurrentTimeMs] = useState(Date.now());
  const [timeOffset, setTimeOffset] = useState(0);

  // Update time every interval
  useEffect(() => {
    const updateTime = () => {
      setCurrentTimeMs(Date.now());
    };

    // Initial update
    updateTime();

    // Set up interval
    const interval = setInterval(updateTime, updateInterval);

    return () => clearInterval(interval);
  }, [updateInterval]);

  // Calculate server-synced time in seconds (accounting for server offset and user's time offset)
  const currentTimeSeconds = timeSyncService.getSyncedTimeSeconds(timeOffset);

  // Function to get time with a specific offset (for components that need different offsets)
  const getTimeWithOffset = useCallback((offset: number): number => {
    return timeSyncService.getSyncedTimeSeconds(offset);
  }, []);

  // Function to update the global time offset
  const updateTimeOffset = useCallback((newOffset: number): void => {
    setTimeOffset(newOffset);
  }, []);

  const value: TimeContextValue = {
    currentTimeMs,
    currentTimeSeconds,
    timeOffset,
    updateTimeOffset,
    getTimeWithOffset,
  };

  return (
    <TimeContext.Provider value={value}>
      {children}
    </TimeContext.Provider>
  );
}

/**
 * useTime - Hook to access the centralized time context
 */
export function useTime(): TimeContextValue {
  const context = useContext(TimeContext);
  if (!context) {
    throw new Error('useTime must be used within a TimeProvider');
  }
  return context;
}

/**
 * useCurrentTimeSeconds - Convenience hook for just the current time in seconds
 */
export function useCurrentTimeSeconds(offset?: number): number {
  const { currentTimeSeconds, getTimeWithOffset } = useTime();

  // If a custom offset is provided, use it; otherwise use the global time
  if (offset !== undefined) {
    return getTimeWithOffset(offset);
  }
  return currentTimeSeconds;
}

export default TimeContext;
